import { useEffect, useState, useRef, MutableRefObject } from 'react';
import { eventStreamService } from 'core-roblox-utilities';
import { debounce } from '../utils/helperUtils';
import { TGameData, TMetaData } from '../types/bedev1Types';
import eventStreamConstants, { PageContext } from '../constants/eventStreamConstants';

type TCalculateViewedGameData = (
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number,
  gameData?: TGameData[],
  metaData?: TMetaData
) => [TGameData[], number];

const gridProperties = {
  paddingSize: 10,
  featureTileDifference: 2 - 1 // Feature Tile Size : Tile Size
};

const gridCalculationViewedGameData: TCalculateViewedGameData = (
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number,
  gameData?: TGameData[],
  metaData?: TMetaData
) => {
  if (tileWidth && tileHeight && gameData && metaData) {
    const numberOfTilesPerRow = Math.floor((width - gridProperties.paddingSize * 2) / tileWidth);
    const numberOfUnseenRows = Math.round(offset / tileHeight);
    const numberOfViewedRows = Math.round((offset + height) / tileHeight);

    let numberOfUnseenTiles = numberOfTilesPerRow * numberOfUnseenRows;
    numberOfUnseenTiles = metaData.emphasis
      ? Math.max(numberOfUnseenTiles - gridProperties.featureTileDifference, 0)
      : numberOfUnseenTiles;

    let numberOfViewedTiles = numberOfTilesPerRow * numberOfViewedRows;
    numberOfViewedTiles = metaData.emphasis
      ? numberOfViewedTiles - gridProperties.featureTileDifference
      : numberOfViewedTiles;

    return [gameData.slice(numberOfUnseenTiles, numberOfViewedTiles), numberOfUnseenTiles];
  }
  return [[], 0];
};

export const grid = {
  ...gridProperties,
  calculateViewedGameData: gridCalculationViewedGameData
};

const newGridProperties = {
  paddingSize: 12,
  horizontalGutterSize: 12,
  verticalGutterSize: 24,
  featureTileDifference: 4 - 1 // Feature Tile Size : Tile Size
};

const newGridCalculationViewedGameData: TCalculateViewedGameData = (
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number,
  gameData?: TGameData[],
  metaData?: TMetaData
) => {
  if (tileWidth && tileHeight && gameData && metaData) {
    const numberOfTilesPerRow = Math.floor(
      (width - newGridProperties.paddingSize) / (tileWidth + newGridProperties.horizontalGutterSize)
    );
    const numberOfUnseenRows = Math.round(
      (offset - newGridProperties.paddingSize) / (tileHeight + newGridProperties.verticalGutterSize)
    );
    const numberOfViewedRows = Math.round(
      (offset + height - newGridProperties.paddingSize) /
        (tileHeight + newGridProperties.verticalGutterSize)
    );

    let numberOfUnseenTiles = numberOfTilesPerRow * numberOfUnseenRows;
    numberOfUnseenTiles = metaData.emphasis
      ? Math.max(numberOfUnseenTiles - newGridProperties.featureTileDifference, 0)
      : numberOfUnseenTiles;

    let numberOfViewedTiles = numberOfTilesPerRow * numberOfViewedRows;
    numberOfViewedTiles = metaData.emphasis
      ? numberOfViewedTiles - newGridProperties.featureTileDifference
      : numberOfViewedTiles;

    return [gameData.slice(numberOfUnseenTiles, numberOfViewedTiles), numberOfUnseenTiles];
  }
  return [[], 0];
};

export const newGrid = {
  ...newGridProperties,
  calculateViewedGameData: newGridCalculationViewedGameData
};

const IEGridProperties = {
  paddingSize: 12,
  horizontalGutterSize: 12,
  verticalGutterSize: 24,
  featureTileDifference: 1 // Feature Tile Size : Tile Size
};

const IEGridCalculationViewedGameData: TCalculateViewedGameData = (
  width: number,
  height: number,
  offset: number,
  tileWidth?: number,
  tileHeight?: number,
  gameData?: TGameData[],
  metaData?: TMetaData
) => {
  if (tileWidth && tileHeight && gameData && metaData) {
    const numberOfTilesPerRow = Math.floor(
      (width - newGridProperties.paddingSize) / (tileWidth + newGridProperties.horizontalGutterSize)
    );
    const numberOfUnseenRows = Math.round(
      (offset - newGridProperties.paddingSize) / (tileHeight + newGridProperties.verticalGutterSize)
    );
    const numberOfViewedRows = Math.round(
      (offset + height - newGridProperties.paddingSize) /
        (tileHeight + newGridProperties.verticalGutterSize)
    );

    const numberOfUnseenTiles = numberOfTilesPerRow * numberOfUnseenRows;
    const numberOfViewedTiles = numberOfTilesPerRow * numberOfViewedRows;

    return [gameData.slice(numberOfUnseenTiles, numberOfViewedTiles), numberOfUnseenTiles];
  }
  return [[], 0];
};

export const IEGrid = {
  ...IEGridProperties,
  calculateViewedGameData: IEGridCalculationViewedGameData
};

export const useGameImpressionGridTracker = <T extends HTMLElement>(
  tileRef: MutableRefObject<T | null>,
  gameData: TGameData[] | undefined,
  metaData: TMetaData | undefined,
  keyword: string,
  session: string,
  calculateViewedGameData: TCalculateViewedGameData
): [number, number, number] => {
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [height, setHeight] = useState<number>(window.innerHeight);
  const [offset, setOffset] = useState<number>(window.pageYOffset);
  const widthRef = useRef<number>(width);
  const heightRef = useRef<number>(height);
  const offsetRef = useRef<number>(offset);
  const gameDataRef = useRef<TGameData[] | undefined>(gameData);
  const metaDataRef = useRef<TMetaData | undefined>(metaData);

  const sendEvent = () => {
    const [viewedGameData, startIndex] = calculateViewedGameData(
      widthRef.current,
      heightRef.current,
      offsetRef.current,
      tileRef.current?.clientWidth,
      tileRef.current?.clientHeight,
      gameDataRef.current,
      metaDataRef.current
    );
    if (viewedGameData.length > 0 && metaDataRef.current) {
      eventStreamService.sendEvent(
        ...((eventStreamConstants.gameImpressions(
          viewedGameData.map(data => data.placeId),
          viewedGameData.map((_, index) => startIndex + index),
          viewedGameData.map(data => (data.isSponsored ? 1 : 0)),
          keyword,
          metaDataRef.current.filteredKeyword,
          metaDataRef.current.correctedKeyword,
          metaDataRef.current.suggestionAlgorithm,
          metaDataRef.current.algorithmQueryType,
          session,
          PageContext.searchPage,
          metaDataRef.current.emphasis ? 1 : 0
        ) as unknown) as Parameters<typeof eventStreamService.sendEvent>)
      );
    }
  };

  const [onScroll, cancelOnScroll] = debounce(() => {
    offsetRef.current = window.pageYOffset;
    heightRef.current = window.innerHeight;
    setOffset(window.pageYOffset);
    setHeight(window.innerHeight);
    sendEvent();
  }, 250);

  const [onResize, cancelOnResize] = debounce(() => {
    widthRef.current = window.innerWidth;
    heightRef.current = window.innerHeight;
    offsetRef.current = window.pageYOffset;
    setOffset(window.pageYOffset);
    setHeight(window.innerHeight);
    setWidth(window.innerWidth);
    sendEvent();
  }, 250);

  const onCleanUp = () => {
    cancelOnScroll();
    cancelOnResize();
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('beforeunload', onCleanUp);
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    window.addEventListener('beforeunload', onCleanUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gameDataRef.current = gameData;
    metaDataRef.current = metaData;
    sendEvent();
  }, [gameData, metaData]);

  return [height, width, offset];
};

export default {
  IEGrid,
  newGrid,
  grid,
  useGameImpressionGridTracker
};
